import React, { FC, Dispatch, SetStateAction } from "react";
import {
  InputLabel,
  FormHelperText,
  FormControl,
  Select,
  Typography,
  Paper,
  Grow,
  Radio,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import { taggedSliderConfigs } from "../../../taggedImages/taggedSliderConfigs";
import ReusableAccordian from "../../ReusableAccordion";
import { RowLayout } from "../../../util/accordianStyles";
import { styled } from "@mui/material/styles";
import useConfigSelector from "./useConfigSelector";

interface SliderConfigProps {
  editQuote: boolean;
  submitPressed: boolean;
  sliderConfig: string;
  setSliderConfig: Dispatch<SetStateAction<string>>;
}

const ColLayoutLeftDivider = styled("div")(({ theme }) => ({
  marginBottom: 10,
  marginTop: 20,
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  borderLeft: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 2),
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  margin: theme.spacing(1),
  minWidth: 240,
  height: 20,
  position: "relative",
  top: -25,
}));

const StyledTypography = styled(Typography)({
  paddingRight: 20,
  fontWeight: "bold",
});

const StyledRowDivAbsolute = styled("div")({
  display: "flex",
  justifyContent: "space-around",
  alignItems: " center",
  position: "absolute",
  top: 70,
  right: 10,
});

const SliderConfigAccordian: FC<SliderConfigProps> = ({
  editQuote,
  submitPressed,
  sliderConfig,
  setSliderConfig,
}) => {
  const {
    sliderConfigOptions,
    handleImgClick,
    handleRadio,
    handleChange,
    borderColor,
    primaryRadio,
    selectedSlider,
    isPocketing,
    chosenConfig,
  } = useConfigSelector(
    sliderConfig,
    setSliderConfig,
    submitPressed,
    editQuote
  );

  return (
    <ReusableAccordian
      accordionName="sliderConfig"
      heading="Configuration"
      secondaryHeading="Select a Configuration"
      required5={true}
      padding={true}
    >
      {sliderConfigOptions.length ? (
        <RowLayout
          style={{
            justifyContent: !sliderConfig ? "flex-start" : "space-around",
            width: !sliderConfig ? "50%" : "100%",
          }}
        >
          <RowLayout>
            <StyledTypography>{isPocketing ? "Pocketing" : "Non-Pocketing"} Options:</StyledTypography>

            <div style={{ position: "relative" }}>
              <div style={{ paddingLeft: 20 }}>
                <StyledFormControl required variant={"outlined"}>
                  <InputLabel>Configuration</InputLabel>

                  <Select
                    style={{ backgroundColor: "white" }}
                    value={sliderConfig}
                    onChange={handleChange}
                    name="configuration"
                    label="Configuration"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {sliderConfigOptions.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>

                  <FormHelperText>Required</FormHelperText>
                </StyledFormControl>
              </div>

              {(sliderConfig === "PXXP" ||
                sliderConfig === "OXXO" ||
                sliderConfig?.includes("-")) && (
                <StyledRowDivAbsolute>
                  <Typography style={{ fontWeight: "bold", paddingRight: 20 }}>
                    Primary Door:{" "}
                    <span style={{ color: "red", fontSize: 20 }}>*</span>
                  </Typography>
                  <FormControl component="fieldset">
                    <FormControlLabel
                      value="end"
                      control={
                        <Radio
                          color="primary"
                          checked={primaryRadio === "Left"}
                          onChange={handleRadio}
                          value="Left"
                        />
                      }
                      label="Left"
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="end"
                      control={
                        <Radio
                          color="primary"
                          checked={primaryRadio === "Right"}
                          onChange={handleRadio}
                          value="Right"
                        />
                      }
                      label="Right"
                      labelPlacement="end"
                    />
                  </FormControl>
                </StyledRowDivAbsolute>
              )}
            </div>
          </RowLayout>
          {sliderConfig && sliderConfigOptions.length && (
            <ColLayoutLeftDivider>
              {!chosenConfig && !sliderConfig?.includes("-") ? (
                <Typography variant="h6" style={{ fontWeight: "bold" }}>
                  Select an Exact Configuration
                </Typography>
              ) : !chosenConfig && sliderConfig?.includes("-") ? (
                <Typography variant="h6" style={{ fontWeight: "bold" }}>
                  Select a Primary Door
                </Typography>
              ) : (
                <Typography
                  variant="h6"
                  style={{ color: borderColor, fontWeight: "bold" }}
                >
                  You Selected {chosenConfig}
                </Typography>
              )}

              <Typography variant="caption">
                (As Viewed from Exterior)
              </Typography>

              <Grow in={sliderConfig ? true : false}>
                <div>
                  {taggedSliderConfigs
                    .filter((item) => item.config === sliderConfig)
                    .map((config) => (
                      <RowLayout
                        key={config.config}
                        style={{ justifyContent: "space-between" }}
                      >
                        <Paper
                          style={{
                            border:
                              selectedSlider === "left" ||
                              config.config?.includes("-")
                                ? `3px solid ${borderColor}`
                                : "",
                          }}
                          onClick={() =>
                            handleImgClick(config.config, "left", config.img1)
                          }
                          elevation={3}
                        >
                          <img src={config.img1} alt="slider" />
                        </Paper>

                        {config.img2 && (
                          <Paper
                            style={{
                              border:
                                selectedSlider === "right"
                                  ? `3px solid ${borderColor}`
                                  : "",
                              marginLeft: 20,
                            }}
                            onClick={() =>
                              handleImgClick(
                                config.config,
                                "right",
                                config.img2
                              )
                            }
                            elevation={3}
                          >
                            <img src={config.img2} alt="slider 2" />
                          </Paper>
                        )}
                      </RowLayout>
                    ))}
                </div>
              </Grow>
            </ColLayoutLeftDivider>
          )}
        </RowLayout>
      ) : (
        <Typography>Enter a Width to View Configuration Options</Typography>
      )}
    </ReusableAccordian>
  );
};

export default SliderConfigAccordian;
